import React, { useEffect, useState, useRef, useCallback } from 'react';
import { User, signOut } from 'firebase/auth';
import { auth } from '../config/firebase';
import api from '../utils/api';
import { OFFLINE_ENABLED } from '../offline/entities';
import { wipeReplicaOnSessionEnd } from '../offline/sessionEnd';
import { AuthContext, SessionExpiryState } from './AuthContext';
import {
  decideSessionEnd,
  recordLastGoodAuth,
  getLastGoodAuth,
  consumeIntentionalSignOut,
  markIntentionalSignOut,
  clearClaimsCache,
  setReauthHint,
} from './offlineAuthGrace';
import { acceptMePermissions, fetchMePermissions, resetMePermissions } from './mePermissions';
import { setSignOutNotice, SignOutNoticeCode } from './signOutNotice';
import {
  cacheSessionPolicyHours,
  getCachedSessionPolicyHours,
  computeSessionDeadline,
  classifySessionExpiry,
  isSessionExpiredError,
  passwordAuthTimeIso,
} from './sessionPolicy';
import { setAiAssistantEnabled } from './featureFlags';
import { isEffectivelyOnline } from '../offline/connectivity';
import { clearRecentlyViewed } from '../pages/PatientList/recentlyViewed';
import { DRAFT_STORAGE_KEY as BARN_OPERATIONS_DRAFT_KEY } from '../pages/Billing/barnOperationsItems';

const DEV_BYPASS = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true';

// Mock Firebase user for dev bypass — backend accepts any token when DEV_BYPASS_AUTH=true
const DEV_MOCK_USER = DEV_BYPASS
  ? ({
      uid: 'dev-uid',
      email: 'dev@localhost',
      displayName: 'Dev User',
      getIdToken: async () => 'dev-bypass-token',
    } as unknown as User)
  : null;

// AuthContext lives in ./AuthContext (firebase-free) so context consumers don't
// transitively import config/firebase. Re-exported here for back-compat.
export { AuthContext } from './AuthContext';

const TRANSIENT_AUTH_ERRORS = [
  'auth/quota-exceeded',
  'auth/network-request-failed',
  'auth/too-many-requests',
  'auth/internal-error',
];

function isTransientAuthError(error: any): boolean {
  const code = error?.code || error?.message || '';
  return TRANSIENT_AUTH_ERRORS.some(e => code.includes(e));
}

/** Patient names and barn billing drafts kept in browser storage must not outlive the session. */
function clearSessionBrowserData() {
  clearRecentlyViewed();
  try {
    localStorage.removeItem(BARN_OPERATIONS_DRAFT_KEY);
  } catch {
    /* storage disabled: nothing was stored */
  }
}

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(DEV_MOCK_USER);
  // Tracks the previously-seen user so we can wipe the offline replica on a
  // genuine logged-in -> null transition (not the initial unauthenticated load).
  const prevUserRef = useRef<User | null>(DEV_MOCK_USER);
  const [loading, setLoading] = useState(!DEV_BYPASS);
  const initializingRef = useRef(false);
  /**
   * Is a provisioning cycle OWED for this session? Armed synchronously by the
   * sign-in event, cleared when `initializeUserInDatabase` finishes.
   *
   * ⚠ NOT the same flag as `initializingRef`, which guards that function against
   * re-entrancy and therefore cannot be set before it runs — and it runs a full
   * second after sign-in, while `App.tsx`'s own `useQuery(['current-user-me'])`
   * and `SubscriptionGuard` both issue an authenticated request on the FIRST
   * render. Keying the hold-off below on the re-entrancy flag signed a stranded
   * signup out ~1000 ms before its own self-heal could run.
   */
  const provisioningWindowRef = useRef(false);
  /** The pending init timer, so a session end can cancel it (see clearInitTimer). */
  const initTimerRef = useRef<number | undefined>(undefined);
  /** One idempotent `POST /public/signup` per session, wherever it is attempted
   *  from (the init cycle, or the interceptor below). */
  const selfHealAttemptedRef = useRef(false);
  /**
   * Has `GET /users/me` ANSWERED for this session? Set the first time it does
   * (the init read, the init cycle's post-signup verify, or the interceptor
   * self-heal's own re-read), re-armed by every sign-in.
   *
   * ⚠ It is what keeps the self-heal from RESURRECTING a deleted identity.
   * `DELETE /users/:id` hard-deletes the `User` row AND the Firebase account, but
   * a cached ID token keeps verifying for ~an hour — so a `USER_NOT_PROVISIONED`
   * can also mean "the row VANISHED", not "the row was never written". Those look
   * identical at the interceptor, and `POST /public/signup` dedupes on the token's
   * uid only: not matching it, it creates a clinic and its OWNER
   * and the deleted user returns as OWNER of a brand-new clinic — re-polluting the
   * database the junk-clinic purge runbook exists to clean. A uid that has already
   * answered is never provisioned again; it is signed out.
   */
  const identityConfirmedRef = useRef(false);
  // A5 offline-auth grace state.
  const [graceMode, setGraceMode] = useState(false);
  const graceModeRef = useRef(false); // mirror for event listeners (stable closures)
  const endingRef = useRef(false); // single-writer guard for the session-end transition
  // ADR-0017 absolute-session-lifetime UX state. 'locked' is sticky: only a
  // successful in-place re-auth (or a fresh sign-in) clears it — never a
  // periodic re-evaluation, so a flaky clock can't unlock a locked session.
  const [sessionExpiry, setSessionExpiry] = useState<SessionExpiryState>('none');
  const [reauthCount, setReauthCount] = useState(0);
  const sessionExpiryRef = useRef<SessionExpiryState>('none');
  const warnDismissedRef = useRef(false);
  const applySessionExpiry = useCallback((next: SessionExpiryState) => {
    sessionExpiryRef.current = next;
    setSessionExpiry(next);
  }, []);

  const getTokenSafe = useCallback(async (firebaseUser: User, forceRefresh = false): Promise<string | null> => {
    try {
      return await firebaseUser.getIdToken(forceRefresh);
    } catch (error: any) {
      if (isTransientAuthError(error)) {
        console.warn('Transient Firebase error, retrying with cached token...', error.code || error.message);
        if (forceRefresh) {
          try {
            return await firebaseUser.getIdToken(false);
          } catch {
            return null;
          }
        }
      }
      console.error('Error getting auth token:', error);
      return null;
    }
  }, []);

  /**
   * Drop the pending provisioning cycle and close its window. Called on every
   * session end: `signOut(auth)` nulls `auth.currentUser`, so a timer that fires
   * afterwards sends `GET /users/me` with NO Bearer token, gets a bare 401 back
   * and logs a misleading "Auth token invalid during user init".
   */
  const clearInitTimer = useCallback(() => {
    if (initTimerRef.current !== undefined) {
      window.clearTimeout(initTimerRef.current);
      initTimerRef.current = undefined;
    }
    provisioningWindowRef.current = false;
  }, []);

  /**
   * A5: idempotent single-writer for a GENUINE session end (revocation, intentional
   * logout, or an expired grace window). Routes through the B3-aware wipe (empty
   * queue → clearReplica; unsynced > 0 → preserve), drops the offline claims
   * cache, ends React state, and signs out of Firebase so auth.currentUser can't
   * keep authing a session we've ended. The endingRef guard makes the null event
   * that signOut() itself re-fires a no-op instead of re-entering grace.
   */
  const endSession = useCallback(async () => {
    if (endingRef.current) return;
    endingRef.current = true;
    clearInitTimer();
    try {
      await wipeReplicaOnSessionEnd();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Offline replica wipe on session end failed:', e);
    }
    clearClaimsCache();
    resetMePermissions();
    clearSessionBrowserData();
    prevUserRef.current = null;
    graceModeRef.current = false;
    setGraceMode(false);
    setUser(null);
    try {
      await signOut(auth);
    } catch {
      /* signOut is local; if it throws, React state is already ended */
    }
  }, [clearInitTimer]);

  /**
   * The provisioning write, in ONE place: `POST /public/signup` checks the bearer
   * token itself (no JwtAuthGuard) and is idempotent (a uid that already has a clinic
   * returns without writing), so
   * both callers below can attempt it safely.
   */
  const provisionUser = useCallback(async (firebaseUser: User) => {
    await api.post('/public/signup', {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      name: firebaseUser.displayName || firebaseUser.email?.split('@')[0],
    });
  }, []);

  /**
   * Last chance before a sign-out: provision the row, take a fresh token (the
   * signup mints role/clinicId claims) and re-read `/users/me`. `true` = the
   * identity is usable now, so the session stays. Deliberately WITHOUT the init
   * cycle's 2 s settle wait — this runs inside an axios error handler, where the
   * caller is already waiting on its own rejection.
   */
  const selfHealProvisioning = useCallback(
    async (firebaseUser: User): Promise<boolean> => {
      try {
        await provisionUser(firebaseUser);
        await getTokenSafe(firebaseUser, true);
        const response = await api.get('/users/me');
        if (prevUserRef.current !== firebaseUser || endingRef.current) return false;
        acceptMePermissions(firebaseUser.uid, response.data?.permissions);
        // The identity answered: from here on a USER_NOT_PROVISIONED is a VANISHED
        // row, and the next one must not POST a signup again (see the ref's note).
        identityConfirmedRef.current = true;
        return true;
      } catch (error) {
        console.error('Provisioning self-heal failed — ending the session:', error);
        return false;
      }
    },
    [getTokenSafe, provisionUser],
  );

  /**
   * A5: is the device genuinely reachable? `navigator.onLine` lies (reads true on
   * a captive-portal / dead-uplink LAN), so don't trust it alone to declare a
   * session dead. Offline ⇒ false. Online ⇒ probe with a token refresh: a
   * `network-request-failed` means we did NOT reach the network (stay
   * offline/grace-eligible); a resolve OR any other (definitive) auth error means
   * we DID reach it (online → genuine end).
   */
  const confirmReachable = useCallback(async (probeUser: User | null): Promise<boolean> => {
    if (!navigator.onLine) return false;
    if (!probeUser) return true;
    try {
      await probeUser.getIdToken(true);
      return true;
    } catch (error: any) {
      // A transient backend error (network-request-failed / quota / rate-limit /
      // internal) means we did NOT get a definitive answer — treat as not-yet-
      // reachable so we stay grace-eligible instead of ending the session on a
      // recoverable blip. Mirrors the reconnect listener's isTransientAuthError
      // gate so both paths classify the same hiccup identically. A definitive
      // error (e.g. user-token-expired) means we reached the server → online.
      return !isTransientAuthError(error);
    }
  }, []);

  /**
   * ADR-0017: where does this session stand against the server's absolute
   * lifetime? Uses the CACHED token result (no network) — `authTime` moves only
   * on a genuine (re)sign-in. Engages only when effectively online: offline the
   * backend gate cannot fire and the A5 grace layer governs, and locking a vet
   * at a barn behind a re-auth that needs the network is the one outcome this
   * design must never produce.
   */
  const evaluateSessionExpiry = useCallback(async () => {
    if (DEV_BYPASS) return;
    const u = prevUserRef.current;
    if (!u || endingRef.current || sessionExpiryRef.current === 'locked') return;
    const hours = getCachedSessionPolicyHours();
    if (hours == null) {
      if (sessionExpiryRef.current !== 'none') applySessionExpiry('none');
      return;
    }
    if (!isEffectivelyOnline()) return;
    let authTime: string | null;
    try {
      // The last PASSWORD sign-in: a PIN unlock's custom token carries it, so the unlock never
      // restarts the clock (the server's passwordAuthTimeOf reads the same claim).
      authTime = passwordAuthTimeIso(await u.getIdTokenResult());
    } catch {
      return; // no definitive read — change nothing (backend stays authoritative)
    }
    const phase = classifySessionExpiry(
      computeSessionDeadline(authTime, hours),
      Date.now(),
    );
    if (phase === 'expired') applySessionExpiry('locked');
    else if (phase === 'warn' && !warnDismissedRef.current) applySessionExpiry('warn');
    else if (phase === 'ok' && sessionExpiryRef.current === 'warn') applySessionExpiry('none');
  }, [applySessionExpiry]);

  /** SessionReauthDialog succeeded: auth_time is fresh — resume in place. */
  const onSessionReauthed = useCallback(() => {
    warnDismissedRef.current = false;
    applySessionExpiry('none');
    // A network-backed re-auth also re-anchors the A5 offline grace window so
    // the two 24h clocks stay in lockstep (both count from the last sign-in).
    if (OFFLINE_ENABLED && navigator.onLine) recordLastGoodAuth(Date.now());
    // The two startup checks that are not React Query answer again: SubscriptionGuard re-runs on
    // the count, and the shared permission store re-reads /users/me for every consumer.
    setReauthCount((n) => n + 1);
    const u = prevUserRef.current;
    if (u) void fetchMePermissions(u.uid).catch(() => undefined);
  }, [applySessionExpiry]);

  const dismissSessionWarning = useCallback(() => {
    warnDismissedRef.current = true;
    if (sessionExpiryRef.current === 'warn') applySessionExpiry('none');
  }, [applySessionExpiry]);

  useEffect(() => {
    let responseInterceptor: number | undefined;

    const requestInterceptor = api.interceptors.request.use(
      async (config) => {
        if (DEV_BYPASS) {
          config.headers.Authorization = 'Bearer dev-bypass-token';
          return config;
        }
        const currentUser = auth.currentUser;
        if (currentUser) {
          const token = await getTokenSafe(currentUser);
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    responseInterceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        // ADR-0017: the backend rejected this session's age. A force-refreshed
        // token carries the SAME auth_time, so the retry below would just 401
        // again — skip it and raise the in-place re-auth lock instead. Never a
        // signOut (that would cascade to every tab and wipe the replica).
        if (isSessionExpiredError(error)) {
          if (prevUserRef.current && !endingRef.current) applySessionExpiry('locked');
          return Promise.reject(error);
        }
        // P2 / INV-104: `jwt.strategy` resolves the identity from Postgres on
        // every request and fails CLOSED. These two refusals are PERMANENT for
        // this identity — the User row is deactivated, or there is no User row
        // for this Firebase uid — so neither the force-refresh retry below nor
        // the SESSION_EXPIRED re-auth dialog above can clear them: signing in
        // again at Firebase succeeds and the very next request 401s identically,
        // which is a loop the user cannot escape. End the session for real and
        // leave Login the reason (a bare login screen after a successful sign-in
        // that bounces is the worst version of this).
        const permanentCode =
          error.response?.status === 401 &&
          (error.response?.data?.code === 'USER_INACTIVE' ||
            error.response?.data?.code === 'USER_NOT_PROVISIONED')
            ? (error.response.data.code as SignOutNoticeCode)
            : null;
        if (permanentCode) {
          // THE FOUR POPULATIONS behind these two codes, and what each one gets.
          // `USER_NOT_PROVISIONED` means "no User row joins this Firebase uid",
          // which is FOUR different situations; the sign-out is right for only
          // two of them, so the order below is the whole design:
          //
          // 1. BRAND-NEW SIGNUP. `useAuth.signup` creates the Firebase account and
          //    THEN posts /public/signup, and the strategy raises this code BEFORE
          //    the email-verify gate — so every request in between carries it,
          //    starting with the one App.tsx fires on its first render. Held off by
          //    `provisioningWindowRef`, which the sign-in event arms synchronously.
          // 2. STRANDED SIGNUP (the two-step signup drop-off): the Firebase account
          //    exists, the row was never written. The init cycle heals it; if the
          //    401 arrives after that window closed, the interceptor attempts the
          //    same idempotent POST once before giving up. Without that a
          //    brand-new clinic owner is told to "contact your clinic
          //    administrator" — and that administrator is them. ⚠ Only while the
          //    identity has NEVER answered (`identityConfirmedRef`): a uid whose
          //    /users/me already succeeded is population 5 below, not this one.
          // 3. EXISTING ROW WITH A NULL `firebaseUid` (the pre-fix updateUserRole
          //    clobber). POST /public/signup writes nothing (it matches only by
          //    the token's uid, and the email collision answers 409), so the
          //    self-heal reports failure and the sign-out happens — correctly: the
          //    repair is server-side (`backfill-user-claims.ts --check` names the
          //    row, `--repair` writes the uid).
          // 4. DEACTIVATED (`USER_INACTIVE`): no window, no self-heal — a
          //    deactivated account is a decision, not a race.
          // 5. DELETED MID-SESSION. The row (and the Firebase account) were
          //    hard-deleted while a cached ID token was still verifying, so an
          //    identity that HAD answered starts raising this code. Never
          //    re-provisioned: POST /public/signup does not match the uid and
          //    would create a new clinic with the deleted user as OWNER.
          //    Straight to the sign-out.
          //
          // Neither code is ever RETRIED: a force-refreshed token resolves against
          // the same Postgres row, and the SESSION_EXPIRED re-auth dialog above
          // cannot clear them either (signing in again at Firebase succeeds and the
          // very next request 401s identically).
          const provisioning =
            permanentCode === 'USER_NOT_PROVISIONED' && provisioningWindowRef.current;
          if (!provisioning && !endingRef.current) {
            if (
              permanentCode === 'USER_NOT_PROVISIONED' &&
              !selfHealAttemptedRef.current &&
              !identityConfirmedRef.current &&
              prevUserRef.current
            ) {
              selfHealAttemptedRef.current = true;
              if (await selfHealProvisioning(prevUserRef.current)) {
                // Population 2: provisioned now. Keep the session; this one
                // request still fails and its caller retries on its own terms.
                return Promise.reject(error);
              }
            }
            // Re-checked after the await: the self-heal's own re-read goes back
            // through this handler, which may already have ended the session.
            if (!endingRef.current) {
              setSignOutNotice(permanentCode);
              // Classify the null this signOut fires as an INTENTIONAL logout so
              // no other tab parks a revoked identity in the A5 grace window.
              if (OFFLINE_ENABLED && prevUserRef.current) {
                markIntentionalSignOut(prevUserRef.current.uid, Date.now());
              }
              await endSession();
            }
          }
          return Promise.reject(error);
        }
        const originalRequest = error.config;
        if (
          error.response?.status === 401 &&
          !originalRequest._retried &&
          auth.currentUser
        ) {
          originalRequest._retried = true;
          const token = await getTokenSafe(auth.currentUser, true);
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          }
        }
        return Promise.reject(error);
      },
    );

    return () => {
      api.interceptors.request.eject(requestInterceptor);
      if (responseInterceptor !== undefined) {
        api.interceptors.response.eject(responseInterceptor);
      }
    };
  }, [getTokenSafe, applySessionExpiry, endSession, selfHealProvisioning]);

  const initializeUserInDatabase = useCallback(async (firebaseUser: User) => {
    if (initializingRef.current) return;
    initializingRef.current = true;

    try {
      const response = await api.get('/users/me');
      if (prevUserRef.current !== firebaseUser || endingRef.current) return;
      acceptMePermissions(firebaseUser.uid, response.data?.permissions);
      // The ordinary case: this identity resolves. Recorded so a later
      // USER_NOT_PROVISIONED is read as a VANISHED row (population 5 above) and
      // never re-provisioned into a ghost clinic.
      identityConfirmedRef.current = true;
      return;
    } catch (error: any) {
      const status = error.response?.status;
      const code = error.response?.data?.code;

      // No User row for this Firebase uid. Since P2 (INV-104) jwt.strategy resolves the row
      // BEFORE any controller runs and answers 401 USER_NOT_PROVISIONED — the controller can
      // never 404 any more — but a backend rolled back to P1 still 404s, so both spellings
      // mean the same thing here: a stranded signup (the two-step signup drop-off) whose
      // POST /public/signup never landed. That route is idempotent on the uid, so heal it.
      // The interceptor above deliberately does NOT sign out on USER_NOT_PROVISIONED while
      // initializingRef is set, which is what lets this arm see the rejection at all.
      const unprovisioned = status === 404 || (status === 401 && code === 'USER_NOT_PROVISIONED');

      if (unprovisioned) {
        // User genuinely doesn't exist in the database — create them. This IS the
        // session's one self-heal attempt, wherever it is made from, so the
        // interceptor does not repeat the POST after this cycle fails.
        selfHealAttemptedRef.current = true;
        try {
          await provisionUser(firebaseUser);

          await getTokenSafe(firebaseUser, true);
          await new Promise(resolve => setTimeout(resolve, 2000));

          try {
            const response = await api.get('/users/me');
            if (prevUserRef.current !== firebaseUser || endingRef.current) return;
            acceptMePermissions(firebaseUser.uid, response.data?.permissions);
            identityConfirmedRef.current = true;
          } catch (verifyError) {
            console.error('User still cannot access protected endpoints after signup:', verifyError);
          }
        } catch (setupError) {
          console.error('Error setting up user:', setupError);
        }
      } else if (status === 401) {
        // Bare 401 (token problem, clinic drift, DB error) — do NOT trigger signup; the next
        // request re-resolves.
        console.warn('Auth token invalid during user init — will retry on next request');
      } else if (status === 403) {
        // Email-verification gate (INV-22): the user exists but hasn't verified
        // their email yet. Not an error — App.tsx routes them to the wall.
      } else {
        console.error('Unexpected error checking user in database:', error);
      }
    } finally {
      initializingRef.current = false;
      // The window closes with the cycle: from here on a USER_NOT_PROVISIONED is
      // about an identity, not about a race.
      provisioningWindowRef.current = false;
    }
  }, [getTokenSafe, provisionUser]);

  useEffect(() => {
    if (DEV_BYPASS) return; // Mock user already set; skip Firebase listener

    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser: User | null) => {
      if (firebaseUser) {
        // Real (or restored) sign-in. Re-arm the single-writer for the next
        // session and exit any grace. Only refresh the grace window on a
        // network-backed event (an offline cold-start restore must not extend it).
        // A workstation PIN swap goes user to user with no null between, so it clears here.
        if (prevUserRef.current && prevUserRef.current.uid !== firebaseUser.uid) clearSessionBrowserData();
        prevUserRef.current = firebaseUser;
        endingRef.current = false;
        graceModeRef.current = false;
        setGraceMode(false);
        // ⚠ BEFORE setUser: this is the render that makes App.tsx issue its own
        // GET /users/me (and SubscriptionGuard its GET /subscription/status), a
        // full second before the init timer below. The hold-off has to exist by
        // then or a not-yet-provisioned user is signed out of their own signup.
        provisioningWindowRef.current = true;
        selfHealAttemptedRef.current = false; // one attempt per SESSION
        // A new (or restored) session has not proven its identity yet — the init
        // read below is what does that. Re-armed here so a second user signing in
        // on the same tab is not judged by the first one's answer.
        identityConfirmedRef.current = false;
        setUser(firebaseUser);
        // A (re)sign-in resets auth_time — clear any session-expiry UX state.
        warnDismissedRef.current = false;
        applySessionExpiry('none');
        if (OFFLINE_ENABLED && navigator.onLine) recordLastGoodAuth(Date.now());
        initTimerRef.current = window.setTimeout(() => {
          initTimerRef.current = undefined;
          void initializeUserInDatabase(firebaseUser);
        }, 1000);
        setLoading(false);
        return;
      }

      // null event — a session is ending by some non-`user` path.
      if (OFFLINE_ENABLED) {
        // Ignore the null that our own endSession()→signOut(auth) re-fires.
        if (endingRef.current) {
          setLoading(false);
          return;
        }
        const prev = prevUserRef.current;
        if (prev) {
          const intentional = consumeIntentionalSignOut(prev.uid, Date.now());
          const reachable = await confirmReachable(prev);
          // Re-check after the await: a concurrent end (e.g. an intentional
          // logout's null) may have started during confirmReachable — don't let
          // a late offline verdict resurrect a session into grace.
          if (endingRef.current) {
            setLoading(false);
            return;
          }
          const verdict = decideSessionEnd({
            offline: !reachable,
            intentional,
            lastGoodAuth: getLastGoodAuth(),
            now: Date.now(),
          });
          if (verdict === 'grace') {
            // Keep the cached session mounted (SAME user ref → useRBAC's [user]
            // effect doesn't re-run → claims/clinicId survive). No wipe, no
            // redirect: the vet keeps working against the replica offline.
            graceModeRef.current = true;
            setGraceMode(true);
            setUser(prev);
            setLoading(false);
            return;
          }
          // Non-intentional end (revocation/expiry) → hint Login to explain the
          // bounce and that unsynced work is preserved for re-sync. An explicit
          // logout sets no hint (the vet chose to leave).
          if (!intentional) setReauthHint();
          await endSession();
          setLoading(false);
          return;
        }
      }

      // Offline disabled, or no prior user (genuine logged-out load). With the
      // offline flag OFF (prod default) this is the ONLY path an involuntary null
      // takes — a cross-tab sign-out or a revoked refresh token — so it must drop
      // the in-memory /users/me permission memo too, or a re-sign-in by the same
      // uid inside its 30 s TTL is served the ENDED session's set with no round
      // trip. Uid-keyed, so never another user's — but authz metadata must not
      // outlive the session. (`clearClaimsCache` is not needed here: nothing
      // writes that cache while the flag is off.) The pending init timer goes too:
      // there is no signed-in user left for it to provision.
      clearInitTimer();
      resetMePermissions();
      clearSessionBrowserData();
      prevUserRef.current = firebaseUser;
      setUser(firebaseUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [initializeUserInDatabase, confirmReachable, endSession, applySessionExpiry, clearInitTimer]);

  // A5: reconnect handling for an active grace window. We don't blindly drop to
  // /login on reconnect (that would needlessly log out a vet whose token is still
  // valid). Probe a real refresh: valid → silently exit grace and stay signed in;
  // a transient/network error → still not reachable, stay in grace; a definitive
  // invalidation → end the session (→ /login; B3 preserves any unsynced queue).
  useEffect(() => {
    if (DEV_BYPASS || !OFFLINE_ENABLED) return;
    const onOnline = async () => {
      if (!graceModeRef.current) return;
      const u = prevUserRef.current;
      if (!u) return;
      try {
        await u.getIdToken(true);
        graceModeRef.current = false;
        setGraceMode(false);
        recordLastGoodAuth(Date.now());
      } catch (error: any) {
        if (isTransientAuthError(error)) return; // not actually reachable yet; stay in grace
        setReauthHint(); // dead token on reconnect → /login with the "sign in to sync" hint
        await endSession();
      }
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [endSession]);

  // ADR-0017: refresh the server's session-lifetime policy (one source of
  // truth) and watch the deadline. Timestamp-deadline checks on interval +
  // wake-style events — background tabs throttle timers, so the visibility/
  // focus/online checks are what actually catch "came back the next morning".
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/public/auth-config');
        if (cancelled) return;
        // Reinsight kill switch rides the same payload — the FAB stays dark
        // until the backend says on. Fetched under DEV_BYPASS too (local dev
        // still needs the assistant); the session policy below is real-auth only.
        setAiAssistantEnabled(data?.aiAssistantEnabled);
        if (DEV_BYPASS) return;
        const hours = data?.sessionMaxAgeHours;
        cacheSessionPolicyHours(
          typeof hours === 'number' && Number.isFinite(hours) && hours > 0 ? hours : null,
        );
      } catch {
        /* offline or transient — keep the cached policy; the AI flag stays dark */
      }
      if (!DEV_BYPASS) evaluateSessionExpiry();
    })();
    if (DEV_BYPASS) {
      return () => {
        cancelled = true;
      };
    }
    const interval = window.setInterval(evaluateSessionExpiry, 60 * 1000);
    const onWake = () => evaluateSessionExpiry();
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    window.addEventListener('online', onWake);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
      window.removeEventListener('online', onWake);
    };
  }, [user, evaluateSessionExpiry]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        graceMode,
        sessionExpiry,
        onSessionReauthed,
        reauthCount,
        dismissSessionWarning,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
